//@flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import Tree from 'react-virtualized-tree';

import type { DirSvcMappingEntry, Dispatch } from 'state/types';
import {
  addOrUpdateMappingEntries,
  queryDirectoryService as queryDirectoryServiceCommand,
} from 'state/commands/dirSvcCommands';
import { actions as dirSvcActions } from 'state/reducers/dirSvcReducer';
import type { DirSvcNode } from 'state/types/dirSvcTypes';
import {
  getDirectoryServiceQueryResults,
  getDirectoryServiceQueryType,
  getIsDirectoryServiceQueryPending,
  getSelectedDirectoryServiceNode,
} from 'state/selectors';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import localizedStrings from 'util/localizedStrings';
import {
  AD_MAPPING_QUERIES,
  isNodeGroup,
  isNodeUser,
  ldapSourceToNumber,
  NAME_QUERY_OP,
  QUERY_TYPE_NAME,
  SOURCE_TYPE_USER_EXCLUSION,
} from 'util/dirSvcUtils';
import SourceNode from './SourceNode';
import LabeledField from 'view/Forms/LabeledField';

const {
  setDirectoryServiceQueryType: setDirectoryServiceQueryTypeAction,
  setSelectedNode: setSelectedNodeAction,
} = dirSvcActions;

type Props = {
  addOrUpdateMappingEntries: typeof addOrUpdateMappingEntries,
  directoryServiceQueryResults: DirSvcNode[],
  getDirectoryServiceQueryResults: typeof getDirectoryServiceQueryResults,
  queryDirectoryServiceCommand: typeof queryDirectoryServiceCommand,
  intl: IntlShape,
  isQueryPending: boolean,
  queryType: Object,
  selectedNode?: DirSvcNode,
  setDirectoryServiceQueryTypeAction: typeof setDirectoryServiceQueryTypeAction,
  setSelectedNodeAction: typeof setSelectedNodeAction,
};

type State = {
  nodes: DirSvcNode[],
  nameQueryOperator: string,
  nameQueryText: string,
};

class SourcesView extends Component<Props, State> {
  handleChange: Function;
  onChangeNameQueryOperator: Function;
  onChangeNameQueryText: Function;
  onChangeQueryType: Function;
  onClickCreateExclusion: Function;
  onClickCreateMapping: Function;
  onKeyDownNameQueryText: Function;
  onSearchByName: Function;

  state = {
    nodes: [],
    nameQueryOperator: NAME_QUERY_OP.CONTAINS,
    nameQueryText: '',
  };

  handleChange = nodes => {
    this.setState({ nodes });
  };

  componentWillReceiveProps(nextProps: Props) {
    if (
      this.props.directoryServiceQueryResults !==
      nextProps.directoryServiceQueryResults
    ) {
      this.setState({ nodes: nextProps.directoryServiceQueryResults });
    }
  }

  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.onChangeQueryType = this.onChangeQueryType.bind(this);
    this.onChangeNameQueryOperator = this.onChangeNameQueryOperator.bind(this);
    this.onChangeNameQueryText = this.onChangeNameQueryText.bind(this);
    this.onClickCreateExclusion = this.onClickCreateExclusion.bind(this);
    this.onClickCreateMapping = this.onClickCreateMapping.bind(this);
    this.onSearchByName = this.onSearchByName.bind(this);
    this.onKeyDownNameQueryText = this.onKeyDownNameQueryText.bind(this);
  }

  componentDidMount() {
    this.props.queryDirectoryServiceCommand();
  }

  onChangeQueryType(e) {
    this.props.setSelectedNodeAction(null);
    this.props.setDirectoryServiceQueryTypeAction(e.target.value);
    if (e.target.value === QUERY_TYPE_NAME) {
      this.setState({ nameQueryText: '', nodes: [] });
    } else {
      this.props.queryDirectoryServiceCommand();
    }
  }

  onSearchByName() {
    this.props.queryDirectoryServiceCommand(
      this.state.nameQueryOperator,
      this.state.nameQueryText
    );
  }

  onChangeNameQueryOperator(e) {
    this.setState({ nameQueryOperator: e.target.value });
  }

  onChangeNameQueryText(e) {
    this.setState({ nameQueryText: e.target.value });
  }

  onClickCreateExclusion(e) {
    const { selectedNode } = this.props;
    if (!selectedNode || !isNodeUser(selectedNode)) {
      return;
    }
    const entry: DirSvcMappingEntry = {
      sourceDN: selectedNode.dn,
      sourceType: SOURCE_TYPE_USER_EXCLUSION,
      roleOverrideGuid: '',
      mappingOrder: '0',
      unsaved: true,
    };
    this.props.addOrUpdateMappingEntries([entry]);
  }

  onClickCreateMapping(e) {
    const { selectedNode } = this.props;
    if (!selectedNode) {
      return;
    }
    const entry: DirSvcMappingEntry = {
      sourceDN: selectedNode.dn,
      sourceType: ldapSourceToNumber(selectedNode.attributes.objectClass),
      roleOverrideGuid: '',
      mappingOrder: '0',
      unsaved: true,
    };
    this.props.addOrUpdateMappingEntries([entry]);
  }

  onKeyDownNameQueryText(e) {
    if (e.key === 'Enter') {
      this.onSearchByName();
      e.stopPropagation();
    }
  }

  renderQueryResults() {
    const {
      isQueryPending,
      queryDirectoryServiceCommand,
      selectedNode,
      setSelectedNodeAction,
    } = this.props;
    return isQueryPending ? (
      <div className="full-page-message" data-test-name="query-pending-message">
        <i className="fa fa-spin fa-circle-o-notch fa-2x fa-fw has-text-primary" />
        <span>&nbsp;</span>
        <FormattedMessage
          {...localizedStrings.DIRECTORY_SERVICE_QUERY_PENDING_MESSAGE}
        />
      </div>
    ) : (
      this.state.nodes && (
        <Tree nodes={this.state.nodes} onChange={this.handleChange}>
          {({ node }) => (
            <SourceNode
              node={node}
              onChange={this.handleChange}
              queryDirectoryServiceCommand={queryDirectoryServiceCommand}
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNodeAction}
            />
          )}
        </Tree>
      )
    );
  }

  render() {
    const {
      intl: { formatMessage },
      queryType,
      selectedNode,
    } = this.props;

    const isNameQuery = queryType === QUERY_TYPE_NAME;
    const formClass =
      'dir-svc-mapping-form-section' + (isNameQuery ? ' name-query' : '');

    const NAME_QUERY_OPERATORS = [
      NAME_QUERY_OP.EQUALS,
      NAME_QUERY_OP.CONTAINS,
      NAME_QUERY_OP.STARTS_WITH,
    ];

    return (
      <div className="page column is-two-fifths bordered-page-column">
        <div className={formClass}>
          <div style={{ margin: 'auto', width: '100%' }}>
            <LabeledField
              label={formatMessage(
                localizedStrings.DIRECTORY_SERVICE_BROWSE_FILTER_LABEL
              )}
              style={{ width: '100%' }}
            >
              <div className="select is-fullwidth">
                <select
                  component="input"
                  data-test-name="querySelect"
                  onChange={this.onChangeQueryType}
                  value={queryType}
                >
                  {Object.keys(AD_MAPPING_QUERIES).map(queryTypeKey => (
                    <option key={queryTypeKey} value={queryTypeKey}>
                      {formatMessage(
                        localizedStrings[AD_MAPPING_QUERIES[queryTypeKey].name]
                      )}
                    </option>
                  ))}
                </select>
              </div>
              {isNameQuery && (
                <div
                  key="nameQueryOptions"
                  className="name-query-options field has-addons"
                >
                  <div className="control">
                    <span className="select is-small">
                      <select
                        component="input"
                        data-test-name="name-query-operator"
                        value={this.state.nameQueryOperator}
                        onChange={this.onChangeNameQueryOperator}
                      >
                        {NAME_QUERY_OPERATORS.map(op => (
                          <option key={op} value={op}>
                            {formatMessage(localizedStrings[op])}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                  <div className="control is-expanded">
                    <input
                      type="text"
                      className="input is-small is-fullwidth"
                      data-test-name="name-query-text"
                      onChange={this.onChangeNameQueryText}
                      onKeyDown={this.onKeyDownNameQueryText}
                    />
                  </div>
                  <div className="control">
                    <span className="buttons buttonsGroup">
                      <a
                        className="button is-primary is-small"
                        data-test-name="name-search-button"
                        onClick={this.onSearchByName}
                      >
                        <FormattedMessage {...localizedStrings.SEARCH} />
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </LabeledField>
          </div>
        </div>
        <div className="dir-svc-mapping-header dir-svc-cell dir-svc-cell-padded">
          <FormattedMessage
            {...localizedStrings.DIRECTORY_SERVICE_BROWSE_ITEMS_LABEL}
          />
        </div>
        <div
          className="dir-svc-mapping-main-section"
          data-test-name="dir-svc-mappings-source"
        >
          {this.renderQueryResults()}
        </div>
        <div className="dir-svc-mapping-controls-section">
          <div className="buttons buttonsGroup is-pulled-left">
            <a
              className="button is-dark is-outlined"
              data-test-name="exclude-user"
              disabled={!selectedNode || !isNodeUser(selectedNode)}
              onClick={this.onClickCreateExclusion}
            >
              <FormattedMessage
                {...localizedStrings.CREATE_USER_EXCLUSION_MAPPING}
              />
              <span className="icon">
                <i className="fa fa-arrow-circle-right" />
              </span>
            </a>
          </div>
          <div className="buttons buttonsGroup is-pulled-right">
            <a
              className="button is-primary"
              data-test-name="create-mapping"
              disabled={
                !selectedNode ||
                (!isNodeUser(selectedNode) && !isNodeGroup(selectedNode))
              }
              onClick={this.onClickCreateMapping}
            >
              <FormattedMessage {...localizedStrings.CREATE_MAPPING} />
              <span className="icon">
                <i className="fa fa-arrow-circle-right fa-lg" />
              </span>
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    directoryServiceQueryResults: getDirectoryServiceQueryResults(state),
    isQueryPending: getIsDirectoryServiceQueryPending(state),
    queryType: getDirectoryServiceQueryType(state),
    selectedNode: getSelectedDirectoryServiceNode(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      addOrUpdateMappingEntries,
      getDirectoryServiceQueryResults,
      queryDirectoryServiceCommand,
      setDirectoryServiceQueryTypeAction,
      setSelectedNodeAction,
    })
)(injectIntl(SourcesView));



// WEBPACK FOOTER //
// ./src/view/Pages/DirectoryServicesPage/SourcesView.js